import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  Activity,
  Zap,
  Database,
  AlertCircle,
  RefreshCw,
  TrendingUp,
  ListChecks,
  Sparkles,
} from 'lucide-react';

import { Dialog } from '../../components/ui/Dialog';
import { selectUserToken } from '../../redux/features/user/userSelectors';

import { useGetQueueDashboardQuery } from './api/queueDashboardApi';
import { SectionBoundary } from './components/SectionBoundary';
import { KpiCard, KpiCardSkeleton } from './components/KpiCard';
import { ThroughputChart } from './components/ThroughputChart';
import { QueueDepthBar } from './components/QueueDepthBar';
import { WorkersStrip } from './components/WorkersStrip';
import { BatchProgressCard } from './components/BatchProgressCard';
import { RecentBatchesTable } from './components/RecentBatchesTable';
import { ActivityFeed } from './components/ActivityFeedItem';
import { FailedJobsList } from './components/FailedJobsList';
import { fmt, fmtRelative } from './utils/format';

// --- Small internal building blocks -----------------------------------------

const SectionHeader = ({ Icon, title, badge, right }) => (
  <div className="flex items-center justify-between gap-3 mb-2">
    <div className="flex items-center gap-2">
      {Icon && <Icon size={14} className="text-darkGrayColor" />}
      <h3 className="text-xs font-semibold uppercase tracking-wider text-darkGrayColor">
        {title}
      </h3>
      {badge != null && (
        <span className="inline-flex items-center justify-center min-w-[20px] px-1.5 h-5 rounded-full text-[10px] font-semibold bg-purpleColor/10 text-purpleColor">
          {badge}
        </span>
      )}
    </div>
    {right}
  </div>
);

const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-lg border border-gray-200 p-4 bg-white hover:shadow-sm transition-all duration-200 ' +
      className
    }
  >
    {children}
  </div>
);

const LivePulse = ({ isFetching, generatedAt }) => (
  <div className="flex items-center gap-3">
    {isFetching ? (
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-darkGrayColor">
        <RefreshCw size={12} className="animate-spin text-purpleColor" />
        refreshing…
      </span>
    ) : (
      <span className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-greenColor">
        <span className="relative flex w-2 h-2">
          <span className="absolute inline-flex w-full h-full rounded-full bg-greenColor opacity-60 animate-ping" />
          <span className="relative inline-flex w-2 h-2 rounded-full bg-greenColor" />
        </span>
        Live
      </span>
    )}
    {generatedAt && (
      <span className="text-[10px] text-darkGrayColor">
        Updated {fmtRelative(generatedAt)} ago
      </span>
    )}
  </div>
);

// --- Sub-views --------------------------------------------------------------

const LoadingSkeleton = () => (
  <div className="flex flex-col gap-4 w-full">
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <KpiCardSkeleton />
      <KpiCardSkeleton />
      <KpiCardSkeleton />
      <KpiCardSkeleton />
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <Card className="md:col-span-2">
        <div className="h-[200px] w-full rounded bg-lightGrayColor animate-pulse" />
      </Card>
      <Card>
        <div className="h-[200px] w-full rounded bg-lightGrayColor animate-pulse" />
      </Card>
    </div>
  </div>
);

const ErrorState = ({ error, onRetry }) => {
  const msg =
    error?.data?.message ||
    error?.error ||
    error?.status ||
    'Failed to load dashboard';
  return (
    <div className="flex flex-col items-center gap-3 py-10 w-full rounded-lg border border-redColor/30 bg-redColor/5">
      <AlertCircle size={20} className="text-redColor" />
      <span className="text-sm text-redColor font-medium">
        {String(msg)}
      </span>
      <button
        type="button"
        onClick={onRetry}
        className="flex items-center gap-1 text-xs px-3 py-1.5 rounded border border-gray-200 bg-white hover:bg-lightGrayColor transition-colors"
      >
        <RefreshCw size={12} />
        Retry
      </button>
    </div>
  );
};

// Sum a numeric field across the queues[] array (nulls → 0).
const sumField = (queues, field) =>
  (queues || []).reduce((acc, q) => acc + (q?.[field] ?? 0), 0);

// --- Main modal -------------------------------------------------------------

export const QueueDashboardModal = ({ isOpen, onClose }) => {
  const token = useSelector(selectUserToken);

  const {
    data,
    error,
    isLoading,
    isFetching,
    refetch,
  } = useGetQueueDashboardQuery(
    { token },
    {
      pollingInterval: 2000,
      skipPollingIfUnfocused: true,
      skip: !isOpen || !token,
    }
  );

  const payload = data?.data;

  const queues = payload?.queues ?? [];
  const workers = payload?.workers ?? {};
  const throughput = payload?.throughput ?? {};
  const batches = payload?.batches ?? { active: [], recent: [] };
  const failedJobs = payload?.failed_jobs ?? { total: 0, recent: [] };
  const activity = payload?.recent_activity ?? [];
  const redis = payload?.redis ?? {};

  const totalQueued = useMemo(() => sumField(queues, 'pending'), [queues]);
  const totalReserved = useMemo(() => sumField(queues, 'reserved'), [queues]);
  const activeBatches = batches.active || [];
  const recentBatches = batches.recent || [];
  const failedTotal = failedJobs.total ?? 0;
  const failedRecentCount = (failedJobs.recent || []).length;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      classes="w-[95vw] max-w-6xl min-h-[70vh]"
      content={
        <div
          data-testid="queue-dashboard-dialog"
          className="flex flex-col w-full gap-4"
        >
          {/* 1. Header ----------------------------------------------------- */}
          <div className="flex items-start justify-between gap-4 w-full pr-8 flex-wrap">
            <div className="flex flex-col gap-1">
              <h2
                id="modal-title"
                className="text-lg font-semibold text-blackColor"
              >
                Background Jobs Dashboard
              </h2>
              <span className="text-xs text-darkGrayColor">
                Live view of queues, workers, and imports
              </span>
            </div>
            <LivePulse
              isFetching={isFetching && !isLoading}
              generatedAt={payload?.generated_at}
            />
          </div>

          <div className="w-full border-t border-gray-200" />

          {/* Error / loading */}
          {error && !payload && (
            <ErrorState error={error} onRetry={refetch} />
          )}
          {isLoading && !payload && !error && <LoadingSkeleton />}

          {payload && (
            <>
              {/* 2. KPI row -------------------------------------------------- */}
              <SectionBoundary label="KPIs">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <KpiCard
                    label="Queued"
                    value={totalQueued}
                    icon={Database}
                    tone="purple"
                    sub={
                      queues.length > 1
                        ? `across ${queues.length} queues`
                        : queues[0]?.name
                        ? `on ${queues[0].name}`
                        : undefined
                    }
                  />
                  <KpiCard
                    label="Running"
                    value={totalReserved}
                    icon={Activity}
                    tone="blue"
                    sub="reserved by workers"
                  />
                  <KpiCard
                    label="Throughput"
                    value={throughput.last_1m ?? 0}
                    icon={Zap}
                    tone="green"
                    sub={`jobs / min · ${fmt(throughput.last_5m)} last 5m`}
                  />
                  <KpiCard
                    label="Failed"
                    value={failedTotal}
                    icon={AlertCircle}
                    tone={failedTotal > 0 ? 'red' : 'gray'}
                    sub={
                      failedRecentCount > 0
                        ? `+${failedRecentCount} recent`
                        : 'no recent failures'
                    }
                  />
                </div>
              </SectionBoundary>

              {/* 3. Charts row ---------------------------------------------- */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SectionBoundary label="Throughput chart">
                  <Card className="md:col-span-2 h-full">
                    <SectionHeader
                      Icon={TrendingUp}
                      title="Throughput (last 15 min)"
                      right={
                        <span className="text-[10px] text-darkGrayColor">
                          1h: {fmt(throughput.last_1h)}
                        </span>
                      }
                    />
                    <ThroughputChart series={throughput.series} />
                  </Card>
                </SectionBoundary>
                <SectionBoundary label="Queue depth">
                  <Card>
                    <SectionHeader Icon={Database} title="Queue depth" />
                    <QueueDepthBar queues={queues} />
                  </Card>
                </SectionBoundary>
              </div>

              {/* 4. Workers strip ------------------------------------------- */}
              <SectionBoundary label="Workers">
                <WorkersStrip workers={workers} />
              </SectionBoundary>

              {/* 5. Active imports ------------------------------------------ */}
              <SectionBoundary label="Active imports">
                <div>
                  <SectionHeader
                    Icon={Activity}
                    title="Active imports"
                    badge={activeBatches.length}
                  />
                  {activeBatches.length === 0 ? (
                    <div className="flex flex-col items-center gap-2 py-8 rounded-lg border border-dashed border-gray-200 bg-lightestGrayColor">
                      <Sparkles size={18} className="text-purpleColor" />
                      <span className="text-xs text-darkGrayColor">
                        No imports running.
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col gap-3">
                      {activeBatches.map(b => (
                        <BatchProgressCard key={b.id} batch={b} />
                      ))}
                    </div>
                  )}
                </div>
              </SectionBoundary>

              {/* 6. Recent batches ------------------------------------------ */}
              <SectionBoundary label="Recent batches">
                <div>
                  <SectionHeader
                    Icon={ListChecks}
                    title="Recent batches"
                    badge={recentBatches.length}
                  />
                  <RecentBatchesTable batches={recentBatches} />
                </div>
              </SectionBoundary>

              {/* 7. Activity + Failed jobs ---------------------------------- */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <SectionBoundary label="Activity feed">
                  <Card className="md:col-span-2 h-full">
                    <SectionHeader
                      Icon={Activity}
                      title="Recent activity"
                      badge={activity.length}
                    />
                    <ActivityFeed items={activity} />
                  </Card>
                </SectionBoundary>
                <SectionBoundary label="Failed jobs">
                  <Card className="h-full">
                    <SectionHeader
                      Icon={AlertCircle}
                      title="Failed jobs"
                      badge={failedTotal}
                    />
                    <FailedJobsList failed={failedJobs} />
                  </Card>
                </SectionBoundary>
              </div>

              {/* 8. Footer / Redis ------------------------------------------ */}
              <div className="flex items-center justify-end pt-2 border-t border-gray-100">
                <span className="text-[10px] text-darkGrayColor">
                  Redis {redis.used_memory_human ?? '—'} ·{' '}
                  {fmt(redis.connected_clients)} clients · up{' '}
                  {fmt(redis.uptime_days)}d
                </span>
              </div>
            </>
          )}
        </div>
      }
    />
  );
};
